import { ChangeDetectionStrategy, Component, ElementRef, ErrorHandler, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'

import { KirjanpitoImageHandlerService, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { NaytettavaKirjaus } from '../../../kirjanpito/kirjanpito.component'

import { KirjaukseenLiitettyjenTiedostojenSivut } from 'app/_jaettu-lemonator/model/kirjanpito'

import { animate, state, style, transition, trigger } from '@angular/animations'
import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { LocalMonth } from '@lemon/core/model/common'
import { AsiakasService, AsiakkaanAvainTiedot } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs'
import { distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

@Component({
  selector: '[app-kirjanpito-tilintarkastus-tosite-nayta-kirjauksen-tositteet]',
  templateUrl: './tilintarkastus-tosite-nayta-kirjauksen-tositteet.component.html',
  styleUrls: ['./tilintarkastus-tosite-nayta-kirjauksen-tositteet.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeInAnimation', [state('in', style({ opacity: 1 })), transition(':enter', [style({ opacity: 0 }), animate(2000)]), transition(':leave', animate(0, style({ opacity: 0 })))]),
    trigger('fastFadeInOutAnimation', [state('in', style({ opacity: 1 })), transition(':enter', [style({ opacity: 0 }), animate(150)]), transition(':leave', animate(150, style({ opacity: 0 })))]),
  ],
  standalone: false,
})
export class KirjanpitoTilintarkastusTositeNaytaKirjauksenTositteet implements OnInit, OnDestroy {
  @ViewChild('mainDiv', { static: false }) mainDivRef: ElementRef<HTMLDivElement>

  private _ngUnsubscribe = new Subject<void>()

  naytettavatTositteetJaSivutObservable: BehaviorSubject<TositeJaNaytettavaSivut[]> = new BehaviorSubject([])

  private _metadataMapForMonth: Map<string, KirjaukseenLiitettyjenTiedostojenSivut> = new Map()

  @Input() naytettavaKirjausObservable: Observable<NaytettavaKirjaus>
  @Input() valittuKuukausiObservable: Observable<LocalMonth>
  @Input() laskuasetuksetObservable: Observable<Laskuasetukset>
  @Input() naytaPoistetutKirjauksetObservable: Observable<boolean>
  @Input() isMainEnhancedOn: boolean = false

  private state: Map<string, Set<string>> = new Map()

  constructor(
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    const valitunKirjauksenAvainObservable = this.naytettavaKirjausObservable.pipe(
      map(naytettavaKirjaus => {
        return naytettavaKirjaus?.kirjaus?.avain || null
      }),
      distinctUntilChanged(),
    )

    interface RawData {
      asiakas: AsiakkaanAvainTiedot
      kirjauksenAvain: string
      laskuasetukset: Laskuasetukset
      sivut: KirjaukseenLiitettyjenTiedostojenSivut
    }

    const rawDataObservable: Observable<RawData> = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.laskuasetuksetObservable, valitunKirjauksenAvainObservable]).pipe(
      switchMap(([asiakas, laskuasetukset, kirjauksenAvain]) => {
        this.naytettavatTositteetJaSivutObservable.next(null)
        this.state.clear()
        if (asiakas && laskuasetukset && kirjauksenAvain) {
          const uri = this._kirjanpitoUriService.annaKirjaukseenLiitettyjenTiedostojenUri(asiakas.avain, kirjauksenAvain)
          const doc = this._firebase.firestoreDoc<KirjaukseenLiitettyjenTiedostojenSivut>(uri)
          const obs = doc.listen().pipe(
            map(sivut => {
              if (sivut) {
                this._metadataMapForMonth.set(kirjauksenAvain, sivut)
              }
              const data: RawData = { asiakas: asiakas, kirjauksenAvain: kirjauksenAvain, sivut: sivut, laskuasetukset: laskuasetukset }
              return data
            }),
          )

          const kakusta = this._metadataMapForMonth.get(kirjauksenAvain)
          if (kakusta) {
            return obs.pipe(startWith({ asiakas: asiakas, kirjauksenAvain: kirjauksenAvain, sivut: kakusta, laskuasetukset: laskuasetukset }))
          }

          return obs
        }
        return of<RawData>(null)
      }),
      distinctUntilChanged((a, b) => {
        if ((a && !b) || (!a && b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a.kirjauksenAvain !== b.kirjauksenAvain) {
          return false
        }
        if (Object.keys(a.sivut?.t || []).length !== Object.keys(b.sivut?.t || []).length) {
          return false
        }
        return true
      }),
    )

    combineLatest([rawDataObservable, this.naytaPoistetutKirjauksetObservable])
      .pipe(
        switchMap(([rawData, naytaPoistetutKirjaukset]) => {
          return this._muunnaNaytetyiksiTositteiksi(rawData, naytaPoistetutKirjaukset)
        }),
        filter(a => {
          if (a?.length) {
            for (const tos of a) {
              const tositteenSivutState = this.state.get(tos.avain)
              if (!tositteenSivutState) {
                return true
              }
              for (const siv of tos.sivut) {
                if (!tositteenSivutState.has(siv.avain)) {
                  return true
                }
              }
            }
            return false
          }
          return true
        }),
        tap(a => {
          if (a) {
            for (const tos of a) {
              const set: Set<string> = new Set()
              for (const siv of tos.sivut) {
                set.add(siv.avain)
              }
              this.state.set(tos.avain, set)
            }
          }
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tositteet => {
        this.naytettavatTositteetJaSivutObservable.next(tositteet)

        if (this.mainDivRef) {
          this.mainDivRef.nativeElement.scrollTo(0, 0)
        }
      })
  }

  private async _muunnaNaytetyiksiTositteiksi(
    rawData: { asiakas: AsiakkaanAvainTiedot; kirjauksenAvain: string; laskuasetukset: Laskuasetukset; sivut: KirjaukseenLiitettyjenTiedostojenSivut },
    naytaPoistetutKirjaukset: boolean,
  ): Promise<TositeJaNaytettavaSivut[]> {
    if (!rawData) {
      return null
    }
    const lisaaminenKesken = rawData.kirjauksenAvain ? this._kirjanpitoImageHandlerService.annaTositteetJoidenLisaaminenOnKesken(rawData.kirjauksenAvain) : null
    const poistoKesken = rawData.kirjauksenAvain ? this._kirjanpitoImageHandlerService.annaTositteetJoidenPoistaminenOnKesken(rawData.kirjauksenAvain) : null
    return this._kirjanpitoImageHandlerService.muunnaTositteiksiJaSivuiksi(rawData.asiakas, rawData.sivut, lisaaminenKesken, poistoKesken, naytaPoistetutKirjaukset)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
